import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import { PrimaryButton } from 'src/presentation/components/molecules/Button';
import MailResponseDialog from 'src/presentation/dialogs/MailResponseDialog';
import ReactToReservationDialog from 'src/presentation/dialogs/ReactToReservationDialog';
import GreenCheckmark from 'src/presentation/components/molecules/GreenCheckmark';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import {
	ReservationDate,
	ReservationPersons,
	ReservationTime,
} from 'src/presentation/components/molecules/ReservationAttributes';
import WorkspaceRow from 'src/presentation/components/molecules/WorkspaceRow';
import { useAuthStore } from 'src/domain/stores/useAuthStore';
import Colors from 'src/core/utils/Colors';
import type { ReservationDetailScreenParams } from 'src/presentation/navigation/types/NavigationTypes';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import BackgroundPattern from 'src/presentation/components/atoms/BackgroundPattern';
import ReservationPreferenceCta from 'src/presentation/components/molecules/ReservationPreferenceCta';
import { useReservationsStore } from 'src/domain/stores/useReservationsStore';
import { useLeaveAppStore } from 'src/domain/stores/useLeaveAppStore';
import Dimens from 'src/core/utils/Dimens';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import { SvgIconType } from '@thermenresorts/icons';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import { HtmlView } from 'src/presentation/components/organisms/HtmlView';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import { isNegativeFormattedAmount, isZeroFormattedAmount } from 'src/core/utils/Numbers';
import Translations from 'src/core/utils/Translations';

export default function ReservationDetailScreen() {
	const { user } = useAuthStore();
	const {
		reservation,
		isLoadingReservation,
		validationErrors,
		replyMessage,
		isReplyingToReservation,
		isReplyDialogVisible,
		isReplySentDialogVisible,
		getApiReservation,
		postApiReservationReply,
		clearValidationError,
		setReplyMessage,
		openReplyDialog,
		closeReplyDialog,
		setReplySentDialogVisible,
		openBarcodeDialog,
	} = useReservationsStore();
	const { confirmLeaving } = useLeaveAppStore();
	const route = useRoute<RouteProp<{ params: ReservationDetailScreenParams }>>();
	const reservationId = route.params?.value;
	const pastOrder = route.params?.pastOrder;
	const tabBarInset = useTabBarInset();

	const replyMessageError = validationErrors[Constants.validationFieldReplyMessage]?.[0] ?? '';

	const getPageData = async () => {
		await getApiReservation({ reservationId: reservationId ?? '' });
	};

	useAppStateScreenListener({
		onFocussed: () => getPageData(),
		onForegrounded: () => getPageData(),
	});

	const callPhonenumber = () => {
		const phoneNumber = reservation?.workspace.contact.phonenumber_no;
		const url = `tel://${phoneNumber}`;
		confirmLeaving({ url, description: Translations.leaveAppDescription });
	};

	const onSubmitReaction = async () => {
		await postApiReservationReply({
			reservationId: reservationId ?? '',
			replyMessage,
			onSuccess: () => {
				setReplySentDialogVisible(true);
				setReplyMessage('');
				closeReplyDialog();
			},
		});
	};

	const barcode = reservation?.barcode ?? null;

	return (
		<View style={styles.screen}>
			<BackgroundPattern />
			{isLoadingReservation ? (
				<LoadingIndicator style={{ flex: Dimens.flex1 }} />
			) : (
				<ScrollView contentContainerStyle={{ paddingBottom: tabBarInset }}>
					{reservation ? (
						<>
							<View style={styles.card}>
								<ThermenImage source={reservation.image_url ? { uri: reservation.image_url } : null} style={styles.cardImage} />
								<View style={{ padding: Dimens.padding20 }}>
									<Text style={styles.sansText}>{reservation.name || Translations.notAvailableShort}</Text>
								</View>
								<View style={styles.section}>
									<View
										style={[
											styles.rowDirection,
											{
												justifyContent: 'space-between',
												paddingBottom: Dimens.padding10,
											},
										]}
									>
										<ReservationDate arrivalDate={reservation.arrival_date} departureDate={reservation.departure_date} />
										<ReservationTime arrivalTime={reservation.arrival_time} departureTime={reservation.departure_time} />
										<ReservationPersons personsCount={reservation.persons_count} />
									</View>
									<WorkspaceRow image={reservation.workspace.logo} title={reservation.workspace.title} />
								</View>

								<ReservationPreferenceCta url={reservation.reservation_preferences_url} />

								{reservation.usps.length > 0 && !pastOrder ? (
									<View style={styles.sectionWithGap}>
										{reservation.usps.map((usp, i) => (
											<View key={i} style={styles.uspRow}>
												<View style={styles.iconSlot}>
													<GreenCheckmark size={Dimens.iconSize14} />
												</View>
												{usp.description ? <HtmlView html={usp.description} style={{ flex: Dimens.flex1 }} /> : null}
											</View>
										))}
									</View>
								) : null}

								<View style={styles.sectionWithGap}>
									<Text style={{ fontWeight: Dimens.fontWeight700 }}>{Translations.reservationId}</Text>
									<View style={styles.reservationIdRow}>
										<SvgIcon name={SvgIconType.voucher} size={Dimens.iconSize16} tintColor={Colors.gray350} />
										<Text style={{ flex: Dimens.flex1 }}>
											{reservation.xplan_reservation_id ?? Translations.reservationUnknown}
										</Text>
									</View>
								</View>

								{reservation.rows.length > 0 ? (
									<View style={styles.sectionWithGap}>
										<Text style={{ fontWeight: Dimens.fontWeight700 }}>{Translations.reservationBooking}</Text>
										{reservation.rows.map((row, i) => {
											const isDiscount = isNegativeFormattedAmount(row.price_total);

											return (
												<View key={i} style={styles.iconRow}>
													<SvgIcon name={SvgIconType.clipboardFilled} size={Dimens.iconSize16} tintColor={Colors.gray350} />
													<Text style={styles.bookingRowName}>{row.name}</Text>
													<Text style={isDiscount ? styles.bookingRowDiscountPrice : styles.bookingRowPrice}>
														{isZeroFormattedAmount(row.price_total) ? Translations.reservationIncluded : row.price_total}
													</Text>
												</View>
											);
										})}
									</View>
								) : null}

								<View style={styles.sectionWithGap}>
									<Text style={{ fontWeight: Dimens.fontWeight700 }}>{Translations.reservationLocation}</Text>
									<View style={styles.iconRow}>
										<SvgIcon name={SvgIconType.locationFilled} size={Dimens.iconSize18} tintColor={Colors.gray350} />
										<View style={{ flex: Dimens.flex1 }}>
											<Text>{reservation.workspace.title}</Text>
											<Text>
												{reservation.workspace.contact.address}, {reservation.workspace.contact.postalcode}{' '}
												{reservation.workspace.contact.city}
											</Text>
										</View>
									</View>
								</View>

								<View style={styles.priceSection}>
									<Text>{Translations.reservationPrice}</Text>
									<Text style={styles.priceTotal}>{reservation.price_total}</Text>
								</View>
							</View>

							<MailResponseDialog
								buttonText={Translations.close}
								text={`
									<p>${Translations.thanksForYourMessageWeWillContactYouByEmailAsSoonAsPossible}</p>
									<p>${Translations.pleaseNoteHaveYouRequestedAChangeYouWillThenReceiveANewConfirmationOfYourAdjustedReservationByEmailFromUsAsSoonAsYouHaveReceivedThisEmailThisChangeWillAlsoBeVisibleInTheAppWithinAnHour}</p>
								`}
								title={Translations.response}
								visible={isReplySentDialogVisible}
								onPress={() => setReplySentDialogVisible(false)}
							/>

							<ReactToReservationDialog
								arrivalDate={reservation.arrival_date}
								arrivalTime={reservation.arrival_time}
								departureDate={reservation.departure_date}
								departureTime={reservation.departure_time}
								emailText={user?.email?.toString() ?? ''}
								isLoading={isReplyingToReservation}
								messageError={replyMessageError}
								messageText={replyMessage}
								nameText={user?.fullName?.toString() ?? ''}
								personsCount={reservation.persons_count}
								reservationId={reservation.xplan_reservation_id ?? ''}
								setMessageText={(text) => {
									setReplyMessage(text);
									clearValidationError(Constants.validationFieldReplyMessage);
								}}
								title={reservation.name}
								visible={isReplyDialogVisible}
								workspaceTitle={reservation.workspace.title}
								onPress={() => closeReplyDialog()}
								onPressClaim={() => onSubmitReaction()}
							/>

							<View style={styles.contactSection}>
								<Text style={styles.containerHeaderText}>{Translations.reservationQuestionOrChange}</Text>
								<View style={styles.greenUnderline} />
								<View style={styles.linkList}>
									<Pressable style={styles.contactLink} onPress={() => openReplyDialog()}>
										<View accessible={true} style={styles.contactLinkRow}>
											<View style={{ width: Dimens.size25 }}>
												<SvgIcon name={SvgIconType.chat} size={Dimens.iconSize18} tintColor={Colors.black} />
											</View>
											<Text style={styles.contactLinkText}>{Translations.reservationSendAMessage}</Text>
										</View>
									</Pressable>
									<Pressable style={styles.contactLink} onPress={callPhonenumber}>
										<View accessible={true} style={styles.contactLinkRow}>
											<View style={{ width: Dimens.size25 }}>
												<SvgIcon name={SvgIconType.phone} size={Dimens.iconSize22} tintColor={Colors.black} />
											</View>
											<Text style={styles.contactLinkText}>
												{Translations.reservationCallUs} {reservation.workspace.contact.phonenumber}
											</Text>
										</View>
									</Pressable>
								</View>
								{barcode !== null && (
									<PrimaryButton
										iconSize={Dimens.iconSize21}
										label={Translations.reservationShowBarcode}
										svgIcon={SvgIconType.scanBarcode}
										onPress={openBarcodeDialog}
									/>
								)}
							</View>
						</>
					) : null}
				</ScrollView>
			)}
		</View>
	);
}

const styles = StyleSheet.create({
	iconSlot: {
		height: Dimens.size24,
		justifyContent: 'center',
	},
	linkList: {
		gap: Dimens.gap10,
		marginBottom: Dimens.padding30,
	},
	bookingRowName: {
		flex: Dimens.flex1,
		paddingRight: Dimens.padding10,
	},
	bookingRowDiscountPrice: {
		color: Colors.green,
		fontWeight: Dimens.fontWeight600,
	},
	bookingRowPrice: {
		color: Colors.gray600,
	},
	card: {
		borderRadius: Dimens.borderRadius4,
		backgroundColor: Colors.white,
		marginHorizontal: Dimens.padding18,
		marginVertical: Dimens.padding20,
	},
	cardImage: {
		height: Dimens.size200,
		width: '100%',
		borderTopLeftRadius: Dimens.borderRadius4,
		borderTopRightRadius: Dimens.borderRadius4,
		backgroundColor: Colors.gray300,
	},
	contactLink: {
		borderBottomWidth: Dimens.borderWidth1,
		borderBottomColor: Colors.gray200,
	},
	contactLinkRow: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: Dimens.padding10,
		gap: Dimens.gap10,
	},
	contactLinkText: {
		fontSize: Dimens.fontSize16,
		fontFamily: Fonts.barlow,
	},
	contactSection: {
		paddingVertical: Dimens.padding20,
		marginHorizontal: Dimens.padding18,
	},
	containerHeaderText: {
		fontSize: Dimens.fontSize22,
		paddingVertical: Dimens.padding10,
		fontFamily: Fonts.ptSerif,
	},
	greenUnderline: {
		backgroundColor: Colors.green,
		width: Dimens.size40,
		height: Dimens.size2,
		marginBottom: Dimens.padding30,
	},
	iconRow: {
		flexDirection: 'row',
		gap: Dimens.gap20,
	},
	priceSection: {
		padding: Dimens.padding20,
		borderTopWidth: Dimens.borderWidth1,
		borderTopColor: Colors.gray100,
		gap: Dimens.gap20,
		justifyContent: 'space-between',
		flexDirection: 'row',
	},
	priceTotal: {
		fontWeight: Dimens.fontWeight700,
		fontSize: Dimens.fontSize16,
	},
	reservationIdRow: {
		flexDirection: 'row',
		justifyContent: 'center',
		alignItems: 'center',
		gap: Dimens.gap20,
	},
	rowDirection: {
		flexDirection: 'row',
	},
	sansText: {
		fontSize: Dimens.fontSize19,
		fontFamily: Fonts.ptSerif,
	},
	screen: {
		flex: Dimens.flex1,
		backgroundColor: Colors.gray50,
	},
	section: {
		padding: Dimens.padding20,
		borderTopWidth: Dimens.borderWidth1,
		borderTopColor: Colors.gray100,
	},
	sectionWithGap: {
		padding: Dimens.padding20,
		borderTopWidth: Dimens.borderWidth1,
		borderTopColor: Colors.gray100,
		gap: Dimens.gap20,
	},
	uspRow: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: Dimens.gap20,
	},
});
